import { faFilter, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@sw-sw/lib-ui';
import React, { useContext, useEffect, useState } from 'react';
import { GoogleMap, LoadScriptNext, Marker } from '@react-google-maps/api';
import { Link } from 'react-router-dom';
import { goToDirections } from '../../../utils/googlemaps';
import { useMapsContext } from '../../../contexts/MapsContext';
import AppContext from '../../../contexts/AppContext';
import Loading from '../../Shared/ResourceIndex/Loading';
import ReactSelectFilter from '../shared/SideFilterSharedSelect/SideFilterSharedSelect';

const markerIcon = {
  path: 'M0,-48c-9.4,0-17,7.6-17,17c0,12.7,17,31,17,31s17-18.3,17-31C17,-40.4,9.4,-48,0,-48z M0,-36a5,5 0 1,1 0,10a5,5 0 1,1 0,-10',
  fillColor: '#DB4437',
  fillOpacity: 1,
  strokeWeight: 1,
  scale: 0.7,
};

const isProperCoordinates = (lat: number, lng: number) => {
  return (
    typeof lat === 'number' &&
    typeof lng === 'number' &&
    !isNaN(lat) &&
    !isNaN(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
};

const ProjectInfoModal = ({
  data,
  onClose,
}: {
  data: any;
  onClose: () => void;
}) => {
  return (
    <div className='dashboard-map__modal-container'>
      <div className='dashboard-map__modal-card'>
        <button className='dashboard-map__modal-close' onClick={onClose}>
          <FontAwesomeIcon
            icon={faTimes}
            className='dashboard-map__modal-close-icon'
          />
        </button>
        <h2 className='dashboard-map__modal-title'>{data.projectName}</h2>
        <div className='dashboard-map__modal-content'>
          {[
            [
              'Status',
              <span className='dashboard-map__status-active'>
                {data.status}
              </span>,
            ],
            ['Client', data.clientName],
            ['Type', data.projectTypeName],
            ['Location', data.location],
            ['Contact', data.primaryContactName],
            ['Inspector', data.inspectorName],
            ['Acres', data.currentAcreage],
            ['Permit #', data.permittee],
          ].map(([label, value], idx) => (
            <div className='dashboard-map__row' key={idx}>
              <div className='dashboard-map__label'>{label}:</div>
              <div className='dashboard-map__value'>{value}</div>
            </div>
          ))}
        </div>
        <div className='dashboard-map__modal-buttons'>
          <Link
            to={`/divisions/${data.divisionId}/projects/${data.id}/inspections`}
            style={{ color: 'inherit', textDecoration: 'none' }}
          >
            <Button primary>View Project</Button>
          </Link>
          <Button
            primary
            onClick={() => goToDirections(data.latitude, data.longitude)}
          >
            Get Directions
          </Button>
        </div>
      </div>
    </div>
  );
};

const FilterMenu = ({ userRole }: { userRole?: string }) => {
  const [open, setOpen] = useState(false);
  const [saveBtnVisibility, setSaveBtnVisibility] = useState<any>('hidden');
  const {
    activeFilter,
    setActiveFilter,
    selectedOptions,
    setSelectedOptions,
    projectFilterOptions,
    inspectorFilterOptions,
    refreshMapData,
    resetData,
  } = useMapsContext();

  const handleSelect = (selectedOptions: any, type: string) => {
    setSelectedOptions((prevState: any) => ({
      ...prevState,
      [type]: selectedOptions,
    }));
    setSaveBtnVisibility('visible');
  };

  const handleSaveButtonClick = () => {
    refreshMapData();
    setSaveBtnVisibility('hidden');
  };

  const resetButtonHandler = () => {
    resetData();
  };

  return (
    <div className='dashboard-map'>
      <div className='dashboard-map__menu'>
        <div
          className='dashboard-map__cog-wrapper'
          onClick={() => setOpen(!open)}
        >
          <FontAwesomeIcon icon={faFilter} className='pad-left' />
        </div>

        <div
          className='dashboard-map__drawer-wrapper'
          style={{ display: open ? 'block' : 'none' }}
        >
          <div className='dashboard-map__drawer'>
            <section>
              <p>Map Filter</p>
              <hr />
              <div className='dashboard-map__drawer__divide-section'>
                <span
                  className={activeFilter === 'project' ? 'active' : ''}
                  onClick={() => {
                    setActiveFilter('project');
                  }}
                >
                  Project Filter
                </span>
                <span
                  className={activeFilter === 'inspector' ? 'active' : ''}
                  onClick={() => {
                    setActiveFilter('inspector');
                  }}
                  style={{
                    display: [
                      'Super Admin',
                      'Admin',
                      'Regional Manager',
                      'Area / Assistant Manager',
                    ].includes(userRole || '')
                      ? 'flex'
                      : 'none',
                  }}
                >
                  Inspector Filter
                </span>
              </div>
              <section
                style={{
                  display: activeFilter === 'project' ? 'block' : 'none',
                }}
              >
                <div className='dashboard-map__filter-dropdowns'>
                  {![
                    'Client User',
                    'Inspector',
                    'SWMP Admin',
                    'CDOT PE/Designee',
                    'Client Inspector',
                    'Supervising Engineer',
                    'Project Engineer',
                    'Regional Manager (External)',
                  ].includes(userRole || '') && (
                    <ReactSelectFilter
                      options={projectFilterOptions.regions}
                      isMulti={false}
                      value={selectedOptions.regions}
                      onChange={(value) => handleSelect(value, 'regions')}
                    />
                  )}
                  <ReactSelectFilter
                    options={projectFilterOptions.divisions}
                    isMulti={true}
                    value={selectedOptions.divisions}
                    onChange={(value) => handleSelect(value, 'divisions')}
                    placeholder='Divisions'
                  />
                  <ReactSelectFilter
                    options={projectFilterOptions.clients}
                    isMulti={true}
                    value={selectedOptions.clients}
                    onChange={(value) => handleSelect(value, 'clients')}
                    placeholder='Clients'
                  />
                  <ReactSelectFilter
                    options={projectFilterOptions.projectTypes}
                    isMulti={true}
                    value={selectedOptions.projectTypes}
                    onChange={(value) => handleSelect(value, 'projectTypes')}
                    placeholder='Project Types'
                  />
                  <ReactSelectFilter
                    options={projectFilterOptions.projectContacts}
                    isMulti={true}
                    value={selectedOptions.projectContacts}
                    onChange={(value) => handleSelect(value, 'projectContacts')}
                    placeholder='Project Contacts'
                  />
                </div>
              </section>
              <section
                style={{
                  display: activeFilter === 'inspector' ? 'block' : 'none',
                }}
              >
                <div className='dashboard-map__filter-dropdowns'>
                  <ReactSelectFilter
                    options={inspectorFilterOptions.regions}
                    isMulti={false}
                    value={selectedOptions.regions}
                    onChange={(value) => handleSelect(value, 'regions')}
                  />
                  {['Super Admin', 'Admin'].includes(userRole || '') && (
                    <ReactSelectFilter
                      options={inspectorFilterOptions.regionalManagers}
                      isMulti={true}
                      value={selectedOptions.regionalManagers}
                      onChange={(value) =>
                        handleSelect(value, 'regionalManagers')
                      }
                      placeholder='Regional Managers'
                    />
                  )}
                  {['Super Admin', 'Admin', 'Regional Manager'].includes(
                    userRole || '',
                  ) && (
                    <ReactSelectFilter
                      options={inspectorFilterOptions.areaManagers}
                      isMulti={true}
                      value={selectedOptions.areaManagers}
                      onChange={(value) => handleSelect(value, 'areaManagers')}
                      placeholder='Area/Assistant Managers'
                    />
                  )}
                  {[
                    'Super Admin',
                    'Admin',
                    'Regional Manager',
                    'Area / Assistant Manager',
                  ].includes(userRole || '') && (
                    <ReactSelectFilter
                      options={inspectorFilterOptions.inspectors}
                      isMulti={true}
                      value={selectedOptions.inspectors}
                      onChange={(value) => handleSelect(value, 'inspectors')}
                      placeholder='Inspectors'
                    />
                  )}
                </div>
              </section>
            </section>

            <div className='dashboard-map__action-buttons'>
              <Button
                primary
                style={{ visibility: saveBtnVisibility }}
                onClick={handleSaveButtonClick}
              >
                Save
              </Button>
              <button onClick={resetButtonHandler}>Reset Filter</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Maps = () => {
  const [showModal, setShowModal] = useState(false);
  const {
    mapData,
    activeFilter,
    selectedOptions,
    searchRef,
    handleSearchFunc,
  } = useMapsContext();
  const [currentProject, setCurrentProject] = useState<any>(null);
  const [currentCoordinates, setCurrentCoordinates] = useState({
    lat: mapData[0]?.latitude || 45.51238,
    lng: mapData[0]?.longitude || -111.796459,
  });
  const [filterWiseHexColor, setFilterWiseHexColor] = useState<any>({});
  const [currentKey, setCurrentKey] = useState<string>('inspctorName');
  const appStore = useContext(AppContext);

  useEffect(() => {
    setFilterWiseHexColor({});
    if (mapData.length > 0) {
      for (const item of mapData) {
        if (
          isProperCoordinates(Number(item.latitude), Number(item.longitude))
        ) {
          setCurrentCoordinates({
            lat: Number(item.latitude),
            lng: Number(item.longitude),
          });
          break;
        }
      }

      if (activeFilter === 'inspector') {
        setCurrentKey('inspectorName');
        const colors: any = {};
        let firstAssigned = false;
        mapData.forEach((item) => {
          if (item.inspectorName) {
            if (!colors[item.inspectorName]) {
              if (!firstAssigned) {
                colors[item.inspectorName] = '#DB4437';
                firstAssigned = true;
              } else {
                let color;
                do {
                  color = Math.floor(Math.random() * 16777215)
                    .toString(16)
                    .padStart(6, '0');
                } while (isTooDark(color));
                colors[item.inspectorName] = `#${color}`;
              }
            }
          }
        });

        setFilterWiseHexColor(colors);
      } else {
        const colors: any = {};
        let firstAssigned = false;

        // Priority-based filter key (assumes all are arrays)
        let filterKey = '';
        if (
          selectedOptions.projectContacts &&
          selectedOptions.projectContacts.length > 0
        ) {
          filterKey = 'primaryContactName';
        } else if (
          selectedOptions.projectTypes &&
          selectedOptions.projectTypes.length > 0
        ) {
          filterKey = 'projectTypeName';
        } else if (
          selectedOptions.clients &&
          selectedOptions.clients.length > 0
        ) {
          filterKey = 'clientName';
        } else if (
          selectedOptions.divisions &&
          selectedOptions.divisions.length > 0
        ) {
          filterKey = 'divisionName';
        }

        if (!filterKey) {
          setFilterWiseHexColor({});
          return;
        }

        setCurrentKey(filterKey);
        mapData.forEach((item) => {
          const key = item[filterKey];
          if (key) {
            if (!colors[key]) {
              if (!firstAssigned) {
                colors[key] = '#DB4437';
                firstAssigned = true;
              } else {
                let color;
                do {
                  color = Math.floor(Math.random() * 16777215)
                    .toString(16)
                    .padStart(6, '0');
                } while (isTooDark(color));
                colors[key] = `#${color}`;
              }
            }
          }
        });

        setFilterWiseHexColor(colors);
      }
    }
  }, [mapData]);

  function isTooDark(hex: string): boolean {
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000; // luminance formula
    return brightness < 50; // tweak threshold (50 = very dark)
  }

  if (!appStore) return <Loading />;

  return (
    <div className='dashboard-map'>
      <FilterMenu userRole={appStore.get('user.roleName')} />
      <input
        className='dashboard-map__search-bar'
        ref={searchRef}
        onChange={handleSearchFunc}
        placeholder='Search'
      />

      <div className='dashboard-map__content'>
        <LoadScriptNext
          googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}
        >
          <GoogleMap
            mapContainerStyle={{ height: '100%', width: '100%' }}
            center={{
              lat: currentCoordinates.lat,
              lng: currentCoordinates.lng,
            }}
            options={{
              disableDefaultUI: true, // disables all controls
              zoomControl: false, // ensure zoom buttons stay hidden
              fullscreenControl: false, // disable full screen
              streetViewControl: false, // disable street view
              mapTypeControl: true, // enable only this one
              gestureHandling: 'greedy',
              restriction: {
                latLngBounds: {
                  north: 85,
                  south: -85,
                  east: 180,
                  west: -180,
                },
                strictBounds: true,
              },
              maxZoom: 18,
              minZoom: 3,
            }}
            zoom={6}
          >
            {mapData.map(
              (item) =>
                isProperCoordinates(
                  Number(item.latitude),
                  Number(item.longitude),
                ) && (
                  <Marker
                    key={item.id}
                    position={{
                      lat: Number(item.latitude),
                      lng: Number(item.longitude),
                    }}
                    icon={{
                      ...markerIcon,
                      fillColor:
                        filterWiseHexColor[item[currentKey]] || '#DB4437',
                    }}
                    title={item.projectName}
                    onClick={() => {
                      setCurrentProject(item);
                      setCurrentCoordinates({
                        lat: Number(item.latitude),
                        lng: Number(item.longitude),
                      });
                      setShowModal(true);
                    }}
                  />
                ),
            )}
          </GoogleMap>
        </LoadScriptNext>
      </div>
      {showModal && (
        <ProjectInfoModal
          onClose={() => setShowModal(false)}
          data={currentProject}
        />
      )}
    </div>
  );
};
export default Maps;
